import { useCallback, useEffect, useRef, useState } from 'react';

import { useFormikContext } from 'formik';

import { useAppSelector } from '../../../../../custom-hooks/useAppSelector';
import { validateBankAccount } from '../../../../../services/bankaccountlookup.service';

interface FieldStates {
  bankName: boolean;
  bankAddress: boolean;
  swift: boolean;
  sortCode: boolean;
  accountNumber: boolean;
  iban: boolean;
}

export type BankAddressLookupStatus = 'idle' | 'loading' | 'succeeded' | 'failed';
export type BankAddressLookupPath = 'iban' | 'sort_code' | null;

interface UseBankAddressLookupReturn {
  triggerValidation: () => void;
  resetLookup: () => void;
  takeIbanDerivedEntryFields: () => boolean;
  lookupStatus: BankAddressLookupStatus;
  lookupPath: BankAddressLookupPath;
}

const emptyFieldStates = (): FieldStates => ({
  bankName: false,
  bankAddress: false,
  swift: false,
  sortCode: false,
  accountNumber: false,
  iban: false,
});

// Hook to lookup bank address based on IBAN or Sort Code.
// All CE-1263 GB IBAN autofill behaviour is gated by gbIbanAutofillEnabled && GB.
// When the flag is off, or country is not GB, behaviour matches pre-CE-1263 master.
export const useBankAddressLookup = (
  setDisabledBankAccountFields: React.Dispatch<React.SetStateAction<FieldStates>>,
  setLoadingFieldsDuringAccountValidation: React.Dispatch<React.SetStateAction<FieldStates>>,
  debounceTimerRef: React.MutableRefObject<NodeJS.Timeout | null>,
  gbIbanAutofillEnabled = false
): UseBankAddressLookupReturn => {
  const { values, setFieldValue } = useFormikContext<any>();
  const orgId = useAppSelector((state) => state.currentApp?.OrgId ?? '');
  const previousIbanRef = useRef<string>('');
  const previousSortCodeRef = useRef<string>('');
  const previousAccountNumberRef = useRef<string>('');
  // Tracks Sort Code + Account Number values written by the last successful IBAN lookup
  // (GB IBAN feature only). Used to skip a redundant debounced lookup.
  const lookupWrittenValuesRef = useRef<{ sortCode: string; accountNumber: string } | null>(null);
  // Whether Sort Code + Account Number currently hold values derived from a successful IBAN
  // lookup (GB IBAN feature only). Read and cleared via takeIbanDerivedEntryFields.
  const ibanDerivedEntryFieldsRef = useRef(false);
  // Monotonically-increasing counter. Incremented at the start of every lookupBankAddress
  // call. After the await, responses whose sequence no longer matches the current value are
  // discarded — this prevents a slow, older request from overwriting state set by a faster,
  // newer one (stale-response / race condition).
  const lookupSequenceRef = useRef(0);
  const [lookupStatus, setLookupStatus] = useState<BankAddressLookupStatus>('idle');
  const [lookupPath, setLookupPath] = useState<BankAddressLookupPath>(null);

  const isGbIbanFeatureActive = (countryCode: string) =>
    gbIbanAutofillEnabled && countryCode === 'GB';

  // GB IBAN: GB<2 check><4 bank code><6 sort code><8 account number> = 22 chars
  const extractGbIbanParts = (iban: string): { sortCode: string; accountNumber: string } | null => {
    if (iban.toUpperCase().startsWith('GB') && iban.length === 22) {
      return { sortCode: iban.slice(8, 14), accountNumber: iban.slice(14, 22) };
    }
    return null;
  };

  const getPopulatedFieldValue = useCallback(
    (displayName: string) => {
      const populatedFields = values.PopulatedAccountFields || [];
      const field = populatedFields.find((field: any) => field.DisplayName === displayName);
      return field?.Value || '';
    },
    [values.PopulatedAccountFields]
  );

  const clearLoadingFields = useCallback(() => {
    setLoadingFieldsDuringAccountValidation(emptyFieldStates());
  }, [setLoadingFieldsDuringAccountValidation]);

  const lookupBankAddress = useCallback(
    async (iban: string, sortCode: string, accountNumber: string) => {
      const thisSequence = ++lookupSequenceRef.current;
      try {
        const countryCode = values.BankCountryCode || '';
        const gbIbanFeature = isGbIbanFeatureActive(countryCode);

        if (!orgId || !countryCode) {
          console.log('Missing required parameters for bank account validation');
          setLookupStatus('idle');
          return;
        }

        // ── GB IBAN feature only: skip incomplete sort-code lookups ──────────
        if (gbIbanFeature && !iban) {
          if (!sortCode) {
            setLookupStatus('idle');
            return;
          }
          if (sortCode.length !== 6 || (accountNumber || '').length !== 8) {
            setLookupStatus('idle');
            return;
          }
        }

        if (gbIbanFeature) {
          setLookupPath(iban ? 'iban' : 'sort_code');
          // Lock Bank Name/Address while in flight; keep prior values visible.
          setDisabledBankAccountFields((prev) => ({ ...prev, bankName: true, bankAddress: true }));
        } else {
          setLookupPath(null);
        }

        setLookupStatus('loading');
        setLoadingFieldsDuringAccountValidation({
          ...emptyFieldStates(),
          bankName: true,
          bankAddress: true,
          swift: true,
        });

        console.log(
          `Looking up bank address for country: ${countryCode} IBAN: ${iban} (and sort code: ${sortCode})`
        );
        const response = await validateBankAccount(orgId, {
          countryCode,
          iban: iban || '',
          sortCode: sortCode || '',
          accountNumber: accountNumber || '',
        });

        // A newer lookup was started while this one was in-flight — discard this response
        // to prevent a slow older request from overwriting state set by the newer one.
        if (thisSequence !== lookupSequenceRef.current) {
          console.debug(
            '[useBankAddressLookup] Discarding stale response (seq %d, current is %d)',
            thisSequence,
            lookupSequenceRef.current
          );
          return;
        }

        console.log('Response from bank account validation: ' + JSON.stringify(response));

        const fieldsSuccessfullyPopulated = emptyFieldStates();

        if (response.valid) {
          if (
            response.bankAddress &&
            typeof response.bankAddress === 'string' &&
            response.bankAddress.trim()
          ) {
            setFieldValue('BankAddress', response.bankAddress);
            fieldsSuccessfullyPopulated.bankAddress = true;
          }

          if (
            response.bankName &&
            typeof response.bankName === 'string' &&
            response.bankName.trim()
          ) {
            setFieldValue('BankName', response.bankName);
            fieldsSuccessfullyPopulated.bankName = true;
          }

          if (gbIbanFeature) {
            // Batch Swift + optional GB IBAN → Sort/Acc writes into one Formik update.
            let bicToApply: string | null = null;
            if (response.bic && typeof response.bic === 'string' && response.bic.trim()) {
              const populatedFields = values.PopulatedAccountFields || [];
              const swiftFieldExists = populatedFields.some(
                (field: any) => field.DisplayName === 'Swift'
              );
              if (swiftFieldExists) {
                bicToApply = response.bic;
                fieldsSuccessfullyPopulated.swift = true;
              } else {
                console.warn(
                  'Swift field not found in PopulatedAccountFields array - this should not happen!'
                );
              }
            } else {
              console.log('BIC value not valid or empty, enabling field for manual entry');
            }

            const currentPath: BankAddressLookupPath = iban ? 'iban' : 'sort_code';
            const gbParts = currentPath === 'iban' ? extractGbIbanParts(iban) : null;
            if (gbParts || bicToApply) {
              const populatedFields = values.PopulatedAccountFields || [];
              const updated = populatedFields.map((f: any) => {
                if (gbParts && f.DisplayName === 'Sort Code') {
                  return { ...f, Value: gbParts.sortCode };
                }
                if (gbParts && f.DisplayName === 'Account Number') {
                  return { ...f, Value: gbParts.accountNumber };
                }
                if (bicToApply && f.DisplayName === 'Swift') {
                  return { ...f, Value: bicToApply };
                }
                return f;
              });
              setFieldValue('PopulatedAccountFields', updated);
              if (gbParts) {
                lookupWrittenValuesRef.current = {
                  sortCode: gbParts.sortCode,
                  accountNumber: gbParts.accountNumber,
                };
              }
            }
          } else {
            // Pre-CE-1263: write Swift via indexed field path.
            if (response.bic && typeof response.bic === 'string' && response.bic.trim()) {
              const populatedFields = values.PopulatedAccountFields || [];
              const swiftFieldIndex = populatedFields.findIndex(
                (field: any) => field.DisplayName === 'Swift'
              );
              if (swiftFieldIndex !== -1) {
                setFieldValue(`PopulatedAccountFields.${swiftFieldIndex}.Value`, response.bic);
                fieldsSuccessfullyPopulated.swift = true;
              } else {
                console.warn(
                  'Swift field not found in PopulatedAccountFields array - this should not happen!'
                );
              }
            } else {
              console.log('BIC value not valid or empty, enabling field for manual entry');
            }
          }

          if (gbIbanFeature && iban) {
            ibanDerivedEntryFieldsRef.current = true;
          }
          setDisabledBankAccountFields(fieldsSuccessfullyPopulated);
          clearLoadingFields();
          setLookupStatus('succeeded');
        } else {
          if (response.errorMessage) {
            console.warn('Bank account validation failed:', response.errorMessage);
          }

          if (gbIbanFeature) {
            // Clear stale values; leave fields unlocked so the user can enter manually.
            setFieldValue('BankName', '', false);
            setFieldValue('BankAddress', '', false);
            setDisabledBankAccountFields(emptyFieldStates());
          } else {
            // Pre-CE-1263: unlock for manual entry; leave prior values in place.
            setDisabledBankAccountFields(emptyFieldStates());
          }
          clearLoadingFields();
          setLookupStatus('failed');
        }
      } catch (error) {
        if (thisSequence !== lookupSequenceRef.current) {
          console.debug(
            '[useBankAddressLookup] Discarding stale error (seq %d, current is %d)',
            thisSequence,
            lookupSequenceRef.current
          );
          return;
        }
        console.warn("Couldn't find bank address by account/IBAN:", error);
        const countryCode = values.BankCountryCode || '';
        const gbIbanFeature = isGbIbanFeatureActive(countryCode);

        if (gbIbanFeature) {
          setFieldValue('BankName', '', false);
          setFieldValue('BankAddress', '', false);
          setDisabledBankAccountFields(emptyFieldStates());
        } else {
          setDisabledBankAccountFields(emptyFieldStates());
        }
        clearLoadingFields();
        setLookupStatus('failed');
      }
    },
    [
      values.BankCountryCode,
      values.PopulatedAccountFields,
      orgId,
      gbIbanAutofillEnabled,
      setFieldValue,
      setDisabledBankAccountFields,
      setLoadingFieldsDuringAccountValidation,
      clearLoadingFields,
    ]
  );

  const triggerValidation = useCallback(() => {
    const currentIban = getPopulatedFieldValue('IBAN');
    const currentSortCode =
      getPopulatedFieldValue('Sort Code') || getPopulatedFieldValue('Routing Number');
    const currentAccountNumber = getPopulatedFieldValue('Account Number');

    if (currentIban || currentSortCode) {
      lookupBankAddress(currentIban, currentSortCode, currentAccountNumber);
    }
  }, [getPopulatedFieldValue, lookupBankAddress]);

  /**
   * Drops all lookup state (CE-1363): pending debounce, in-flight responses, field locks and
   * spinners. Used when the bank country changes so nothing resolved for the old country leaks.
   */
  const resetLookup = useCallback(() => {
    lookupSequenceRef.current += 1;
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    previousIbanRef.current = '';
    previousSortCodeRef.current = '';
    previousAccountNumberRef.current = '';
    lookupWrittenValuesRef.current = null;
    ibanDerivedEntryFieldsRef.current = false;
    setLookupStatus('idle');
    setLookupPath(null);
    setDisabledBankAccountFields(emptyFieldStates());
    clearLoadingFields();
  }, [debounceTimerRef, setDisabledBankAccountFields, clearLoadingFields]);

  /**
   * Returns whether Sort Code + Account Number were filled in by a successful IBAN lookup
   * (GB IBAN feature only), and clears that flag. Called when the user edits either field:
   * if both were IBAN-derived, the sibling is cleared too, since it no longer matches.
   */
  const takeIbanDerivedEntryFields = useCallback(() => {
    const wasIbanDerived = ibanDerivedEntryFieldsRef.current;
    ibanDerivedEntryFieldsRef.current = false;
    return wasIbanDerived;
  }, []);

  useEffect(() => {
    const countryCode = values.BankCountryCode || '';
    const gbIbanFeature = isGbIbanFeatureActive(countryCode);

    const currentIban = getPopulatedFieldValue('IBAN');
    const currentSortCode =
      getPopulatedFieldValue('Sort Code') || getPopulatedFieldValue('Routing Number');
    const currentAccountNumber = getPopulatedFieldValue('Account Number');

    // GB IBAN feature: skip debounce when Sort/Acc were just written by an IBAN lookup.
    if (gbIbanFeature && lookupWrittenValuesRef.current) {
      const written = lookupWrittenValuesRef.current;
      lookupWrittenValuesRef.current = null;
      if (currentSortCode === written.sortCode && currentAccountNumber === written.accountNumber) {
        previousIbanRef.current = currentIban;
        previousSortCodeRef.current = currentSortCode;
        previousAccountNumberRef.current = currentAccountNumber;
        return () => {
          if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
          }
        };
      }
    }

    const ibanChanged = currentIban && currentIban !== previousIbanRef.current;
    const sortCodeChanged = currentSortCode && currentSortCode !== previousSortCodeRef.current;
    // Account Number debounce only for GB IBAN feature (master only watched IBAN/Sort Code).
    const accountNumberChanged =
      gbIbanFeature && currentAccountNumber !== previousAccountNumberRef.current;

    if (ibanChanged || sortCodeChanged || accountNumberChanged) {
      previousIbanRef.current = currentIban;
      previousSortCodeRef.current = currentSortCode;
      if (gbIbanFeature) {
        previousAccountNumberRef.current = currentAccountNumber;
      }
      setLookupStatus('idle');

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        lookupBankAddress(currentIban, currentSortCode, currentAccountNumber);
      }, 1000);
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [
    getPopulatedFieldValue,
    lookupBankAddress,
    debounceTimerRef,
    values.BankCountryCode,
    gbIbanAutofillEnabled,
  ]);

  return {
    triggerValidation,
    resetLookup,
    takeIbanDerivedEntryFields,
    lookupStatus,
    lookupPath,
  };
};
